import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CustomCalendar = ({ availableDates, selectedDate, selectedDates = [], onSelectDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const monthNames = {
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    da: ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'],
    de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
  };

  const weekdayNames = {
    en: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    da: ['M', 'T', 'O', 'T', 'F', 'L', 'S'],
    de: ['M', 'D', 'M', 'D', 'F', 'S', 'S']
  };
  
  const currentLang = localStorage.getItem('i18nextLng') || 'de';

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Adjust so Monday is 0

    const days = [];

    // Previous month's days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthLastDay - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }

    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day,
        isCurrentMonth: true,
        date: new Date(year, month, day)
      });
    }

    // Next month's days
    const remainingDays = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        day,
        isCurrentMonth: false,
        date: new Date(year, month + 1, day)
      });
    }

    return days;
  };

  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isDateAvailable = (date) => {
    return availableDates.some(availDate => {
      // Handle both Date objects and string dates
      let d;
      if (availDate instanceof Date) {
        d = availDate;
      } else {
        // Parse date string as local date to avoid timezone issues
        const [year, month, day] = availDate.split('-').map(Number);
        d = new Date(year, month - 1, day);
      }
      return d.toDateString() === date.toDateString();
    });
  };

  const isDateSelected = (date) => {
    // Check if date is in selectedDates array
    if (selectedDates && selectedDates.length > 0) {
      return selectedDates.some(d => d.toDateString() === date.toDateString());
    }
    // Fallback to single selectedDate
    if (!selectedDate) return false;
    return selectedDate.toDateString() === date.toDateString();
  };

  const isDateDisabled = (date) => {
    return date < today;
  };

  const isToday = (date) => {
    return date.toDateString() === today.toDateString();
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (dayObj) => {
    if (!dayObj.isCurrentMonth || isDateDisabled(dayObj.date)) return;
    onSelectDate(dayObj.date);
  };

  return (
    <div className="custom-calendar" data-testid="custom-calendar">
      <div className="calendar-header">
        <button 
          onClick={handlePrevMonth} 
          className="calendar-nav-btn"
          data-testid="prev-month-btn"
        >
          <ChevronLeft size={24} />
        </button>
        <h3>{monthNames[currentLang][currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
        <button 
          onClick={handleNextMonth} 
          className="calendar-nav-btn"
          data-testid="next-month-btn"
        >
          <ChevronRight size={24} />
        </button>
      </div>
      
      <div className="calendar-weekdays">
        {weekdayNames[currentLang].map((day, index) => (
          <div key={index} className="calendar-weekday">{day}</div>
        ))}
      </div>
      
      <div className="calendar-days">
        {days.map((dayObj, index) => {
          const available = isDateAvailable(dayObj.date);
          const selected = isDateSelected(dayObj.date);
          const disabled = isDateDisabled(dayObj.date);
          const todayClass = isToday(dayObj.date);
          
          return (
            <div
              key={index}
              className={`calendar-day ${
                !dayObj.isCurrentMonth ? 'other-month' : ''
              } ${
                available ? 'available' : ''
              } ${
                selected ? 'selected' : ''
              } ${
                disabled ? 'disabled' : ''
              } ${
                todayClass ? 'today' : ''
              }`}
              onClick={() => handleDateClick(dayObj)}
              data-testid={`calendar-day-${dayObj.date.toISOString().split('T')[0]}`}
            >
              {dayObj.day}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomCalendar;